import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Briefcase,
  MapPin,
  Clock,
  Building2,
  Search,
  Filter,
  ChevronRight,
  DollarSign,
  Users,
  Star,
  ExternalLink
} from 'lucide-react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Job Categories
const CATEGORIES = ['All', 'Coaching', 'Administration', 'Marketing', 'Medical', 'Technical', 'Management', 'Operations'];
const TYPES = ['All', 'Full-time', 'Part-time', 'Contract', 'Internship', 'Volunteer'];

// Sample Jobs Data (would come from API)
const SAMPLE_JOBS = [
  {
    id: '1',
    title: 'Head Coach - Football',
    organization: 'Kenya Football Federation',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    category: 'Coaching',
    salary: 'KES 200,000 - 350,000',
    description: 'Leading national team coaching position requiring UEFA Pro License and 10+ years experience.',
    requirements: ['UEFA Pro License', '10+ years coaching', 'International experience'],
    posted: '2 days ago',
    deadline: '2026-04-15',
    featured: true
  },
  {
    id: '2',
    title: 'Sports Marketing Manager',
    organization: 'Basketball Africa League',
    location: 'Kigali, Rwanda',
    type: 'Full-time',
    category: 'Marketing',
    salary: 'USD 4,000 - 6,000',
    description: 'Drive marketing initiatives for BAL across the African continent.',
    requirements: ['5+ years marketing', 'Sports industry experience', 'MBA preferred'],
    posted: '1 week ago',
    deadline: '2026-04-01',
    featured: true
  },
  {
    id: '3',
    title: 'Sports Physiotherapist',
    organization: 'Tusker FC',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    category: 'Medical',
    salary: 'KES 120,000 - 180,000',
    description: 'Provide physiotherapy services to professional football team.',
    requirements: ['BSc Physiotherapy', '3+ years sports experience', 'Licensed'],
    posted: '3 days ago',
    deadline: '2026-03-30',
    featured: false
  },
  {
    id: '4',
    title: 'Stadium Operations Manager',
    organization: 'Kasarani Sports Complex',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    category: 'Operations',
    salary: 'KES 150,000 - 200,000',
    description: 'Oversee all stadium operations for major sports venue.',
    requirements: ['Facility management experience', '5+ years operations', 'Event management'],
    posted: '5 days ago',
    deadline: '2026-04-10',
    featured: false
  },
  {
    id: '5',
    title: 'Youth Academy Director',
    organization: 'Gor Mahia FC',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    category: 'Management',
    salary: 'KES 180,000 - 250,000',
    description: 'Lead youth development program for top Kenyan football club.',
    requirements: ['Youth coaching license', '8+ years experience', 'Player development track record'],
    posted: '1 week ago',
    deadline: '2026-04-20',
    featured: false
  },
  {
    id: '6',
    title: 'Sports Data Analyst Intern',
    organization: 'Michezonet',
    location: 'Remote',
    type: 'Internship',
    category: 'Technical',
    salary: 'KES 30,000 stipend',
    description: 'Join our data team to analyze sports performance metrics.',
    requirements: ['Statistics/Data Science student', 'Python/R skills', 'Sports enthusiasm'],
    posted: '2 days ago',
    deadline: '2026-03-25',
    featured: false
  }
];

const JobsPage = () => {
  const [jobs, setJobs] = useState(SAMPLE_JOBS);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedJob, setSelectedJob] = useState(null);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         job.organization.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || job.category === selectedCategory;
    const matchesType = selectedType === 'All' || job.type === selectedType;
    return matchesSearch && matchesCategory && matchesType;
  });

  const featuredJobs = filteredJobs.filter(job => job.featured);
  const regularJobs = filteredJobs.filter(job => !job.featured);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block bg-primary/20 text-primary px-4 py-1.5 rounded-full text-sm font-body mb-6">
              Sports Careers
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Find Your Dream Job in Sports
            </h1>
            <p className="text-white/70 text-lg font-body mb-8">
              Discover career opportunities across Africa&apos;s sports industry - from coaching 
              to management, marketing to medical roles.
            </p>
          </motion.div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search jobs, organizations..."
                className="w-full bg-card-bg border border-border focus:border-primary rounded-xl pl-12 pr-4 py-4 text-white font-body outline-none"
                data-testid="jobs-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2 text-white/60">
              <Filter size={18} />
              <span className="font-body text-sm">Filter:</span>
            </div>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-body transition-all ${
                    selectedCategory === category
                      ? 'bg-primary text-white'
                      : 'bg-card-bg border border-border text-white/60 hover:text-white'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Type Filter */}
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="bg-card-bg border border-border rounded-lg px-4 py-2 text-white font-body outline-none"
            >
              {TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Jobs Listing */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Jobs List */}
            <div className="lg:col-span-2 space-y-6">
              {/* Featured Jobs */}
              {featuredJobs.length > 0 && (
                <div className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Star className="text-secondary" size={20} />
                    Featured Opportunities
                  </h2>
                  <div className="space-y-4">
                    {featuredJobs.map((job) => (
                      <JobCard 
                        key={job.id} 
                        job={job} 
                        featured 
                        onClick={() => setSelectedJob(job)}
                        isSelected={selectedJob?.id === job.id}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Jobs */}
              <div>
                <h2 className="font-heading text-xl font-bold text-white mb-4">
                  {filteredJobs.length} Jobs Found
                </h2>
                <div className="space-y-4">
                  {regularJobs.map((job) => (
                    <JobCard 
                      key={job.id} 
                      job={job}
                      onClick={() => setSelectedJob(job)}
                      isSelected={selectedJob?.id === job.id}
                    />
                  ))}
                </div>
              </div>

              {filteredJobs.length === 0 && (
                <div className="text-center py-12 bg-card-bg rounded-xl border border-border">
                  <Briefcase className="w-12 h-12 text-white/30 mx-auto mb-4" />
                  <p className="text-white/60 font-body">No jobs found matching your criteria</p>
                </div>
              )}
            </div>

            {/* Job Details Panel */}
            <div className="lg:col-span-1">
              {selectedJob ? (
                <JobDetailsPanel job={selectedJob} />
              ) : (
                <div className="bg-card-bg border border-border rounded-xl p-6 sticky top-24">
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 text-white/30 mx-auto mb-4" />
                    <p className="text-white/60 font-body">Select a job to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Post Job CTA */}
      <section className="py-16 bg-gradient-to-r from-primary/20 to-secondary/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Looking to Hire?
          </h2>
          <p className="text-white/70 font-body mb-6">
            Post your sports job openings and reach thousands of qualified candidates across Africa.
          </p>
          <Link to="/dashboard/jobs/post" className="btn-primary">
            Post a Job
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Job Card Component
const JobCard = ({ job, featured = false, onClick, isSelected }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    onClick={onClick}
    className={`bg-card-bg border rounded-xl p-5 cursor-pointer transition-all ${
      featured ? 'border-secondary/50 bg-gradient-to-r from-secondary/10 to-transparent' :
      isSelected ? 'border-primary' : 'border-border hover:border-primary/50'
    }`}
    data-testid={`job-card-${job.id}`}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          {featured && (
            <span className="bg-secondary/20 text-secondary text-xs font-bold px-2 py-0.5 rounded">
              FEATURED
            </span>
          )}
          <span className="bg-primary/20 text-primary text-xs px-2 py-0.5 rounded">
            {job.type}
          </span>
        </div>
        <h3 className="font-heading text-lg font-bold text-white mb-1">{job.title}</h3>
        <p className="text-primary font-body text-sm mb-2">{job.organization}</p>
        
        <div className="flex flex-wrap items-center gap-4 text-white/50 text-sm">
          <span className="flex items-center gap-1">
            <MapPin size={14} />
            {job.location}
          </span>
          <span className="flex items-center gap-1">
            <DollarSign size={14} />
            {job.salary}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {job.posted}
          </span>
        </div>
      </div>
      <ChevronRight className="text-white/30 flex-shrink-0" size={20} />
    </div>
  </motion.div>
);

// Job Details Panel Component
const JobDetailsPanel = ({ job }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    className="bg-card-bg border border-border rounded-xl p-6 sticky top-24"
  >
    <div className="mb-6">
      <span className="bg-primary/20 text-primary text-xs px-2 py-1 rounded">{job.type}</span>
      <h2 className="font-heading text-2xl font-bold text-white mt-3 mb-2">{job.title}</h2>
      <p className="text-primary font-body">{job.organization}</p>
    </div>

    <div className="space-y-4 mb-6">
      <div className="flex items-center gap-3 text-white/70">
        <MapPin size={18} className="text-white/40" />
        <span className="font-body">{job.location}</span>
      </div>
      <div className="flex items-center gap-3 text-white/70">
        <DollarSign size={18} className="text-white/40" />
        <span className="font-body">{job.salary}</span>
      </div>
      <div className="flex items-center gap-3 text-white/70">
        <Clock size={18} className="text-white/40" />
        <span className="font-body">Deadline: {job.deadline}</span>
      </div>
    </div>

    <div className="mb-6">
      <h3 className="font-heading text-sm font-bold text-white mb-2">Description</h3>
      <p className="text-white/60 font-body text-sm">{job.description}</p>
    </div>

    <div className="mb-6">
      <h3 className="font-heading text-sm font-bold text-white mb-2">Requirements</h3>
      <ul className="space-y-2">
        {job.requirements.map((req, i) => (
          <li key={`${job.id}-req-${i}-${req.slice(0, 15)}`} className="flex items-start gap-2 text-white/60 font-body text-sm">
            <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2" />
            {req}
          </li>
        ))}
      </ul>
    </div>

    <button className="w-full btn-primary flex items-center justify-center gap-2">
      Apply Now <ExternalLink size={16} />
    </button>
  </motion.div>
);

export default JobsPage;
